import { ContractsGrouping, ContractState } from '@ail/contracts-api'
import { ChartData } from 'chart.js'
import { useMemo, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useGetContractStatisticsQuery } from '../../../redux/api/contractsApi'
import { useFilteredCustomers } from '../../../hooks/useFilteredCustomers'
import { normalizeString } from '../../../helpers/Utils'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'

interface Props {
  tenantId?: string
  companyId?: string
  treeview?: boolean
  color?: 'green' | 'magenta'
}

const options = {
  maintainAspectRatio: false,
  responsive: true,
  scales: {
    x: {
      grid: {
        lineWidth: 0,
      },
    },
  },
  plugins: {
    datalabels: {
      color: 'white',
    },
    legend: {
      display: false,
    },
  },
}

const initialData: ChartData<'bar'> = {
  labels: [],
  datasets: [],
}

export default function ByMakesChart({ companyId, color, tenantId, treeview }: Props) {
  const activeState = [ContractState.Running, ContractState.Extended]
  const [selectedState, setSelectedState] = useState<ContractState[]>(activeState)
  const stats = useGetContractStatisticsQuery({
    grouping: ContractsGrouping.ByVehicleMake,
    statuses: selectedState,
  })
  const { t } = useTranslation('common')
  const { customers } = useFilteredCustomers()
  const relatedCustomers = useMemo(() => {
    if (!tenantId && !companyId) return
    return (
      customers
        ?.filter((c) =>
          companyId
            ? treeview
              ? c.customer?.id === companyId || c.customer?.parentId === companyId
              : c.customer?.id === companyId
            : c.customer?.tenantId === tenantId
        )
        .map((c) => c.customer?.id) ?? []
    )
  }, [companyId, customers, tenantId, treeview])

  const aggregatedData = useMemo(() => {
    if (!stats.data) return

    const calculatedStats: { [key: string]: number } = {}

    Object.entries(stats.data).forEach(([key, value]) => {
      if (!relatedCustomers || relatedCustomers.some((id) => id && key.toLowerCase().includes(id))) {
        Object.entries(value).forEach(([prodKey, prodValue]) => {
          calculatedStats[normalizeString(prodKey)] = (calculatedStats[normalizeString(prodKey)] || 0) + prodValue
        })
      }
    })
    return calculatedStats
  }, [relatedCustomers, stats.data])

  const chartData = useMemo(() => {
    if (!aggregatedData) {
      return initialData
    }

    const labels: string[] = []
    const values: number[] = []

    Object.entries(aggregatedData).forEach(([key, value]) => {
      labels.push(key)
      values.push(value)
    })
    const data = {
      labels,
      datasets: [
        {
          label: '',
          data: values,
          backgroundColor: [
            '#c3d583',
            '#a2cd7e',
            '#99D98C',
            '#76C893',
            '#52B69A',
            '#34A0A4',
            '#168AAD',
            '#1A759F',
            '#1E6091',
            '#184E77',
          ],
        },
      ],
    }
    return data
  }, [aggregatedData])

  function handleSelect(s: string) {
    setSelectedState(s.split(',') as ContractState[])
  }
  return (
    <>
      <div className={`box-title ${color}-title`}>
        <span>
          <i className="icon-statistics"></i>
          {t('BY_MAKES')}
        </span>
        <div className="selectArea">
          <select
            defaultValue={activeState}
            className="filterByExpiration"
            onChange={(s) => handleSelect(s.target.value)}
          >
            <option value={activeState}>{t('ACTIVE')}</option>
            <option value={ContractState.Closed}>{t('CLOSED')}</option>
            <option value={ContractState.Initialized}>{t('INITIALIZED')}</option>
            <option value={ContractState.Terminated}>{t('TERMINATED')}</option>
          </select>
          <i className="icon-bottom" />
        </div>
      </div>
      <div style={{ height: '400px' }} className="box3 white">
        {!customers ? (
          <div style={{ marginLeft: '50%', marginTop: '50%' }}>
            <LoadingSpinner size={14} />
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </>
  )
}
